<template>
  <modal>
    <template slot="header">
      <md-button
        class="md-simple md-just-icon md-round modal-default-button"
        @click="closeProjectEditor"
      >
        <md-icon>clear</md-icon>
      </md-button>
    </template>

    <template slot="body">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-primary">
          <div class="card-icon">
            <md-icon>business</md-icon>
          </div>
          <h4 class="title" style="text-align: left">
            {{ $t("Project") }}
          </h4>
        </md-card-header>

        <md-card-content>
          <validated-text-input
            name="projectName"
            v-model="projectName"
            placeholder="Name"
            label="Name"
            validation-rules="required|min:3"
          />

          <validated-dropdown-input
            v-model="selectedSuperuserEmails"
            @input="onSuperusersSelected"
            :options="validSuperUsers"
            label="Super Users"
            name="SuperUser"
            placeholder="Select Super Users"
            :multiselect="true"
            :value-transformation="item => item.email"
            :display-transformation="
              item => `${item.firstname} ${item.lastname}`
            "
          />

          <validated-dropdown-input
            v-model="selectedPilotEmails"
            @input="onPilotsSelected"
            :options="validPilots"
            label="Pilots"
            name="pilots"
            placeholder="Select pilots"
            validation-rules="required"
            :multiselect="true"
            :value-transformation="item => item.email"
            :display-transformation="
              item => `${item.firstname} ${item.lastname}`
            "
          />

          <validated-dropdown-input
            v-model="selectedObserverEmails"
            @input="onObserversSelected"
            :options="observers"
            label="Observers"
            name="observers"
            placeholder="Select observers"
            :multiselect="true"
            :value-transformation="item => item.email"
            :display-transformation="
              item => `${item.firstname} ${item.lastname}`
            "
          />

          <validated-dropdown-input
            v-model="selectedPayloadOperatorEmails"
            @input="onPayloadOperatorsSelected"
            :options="payloadOperators"
            label="Payload operators"
            name="payloadOperators"
            placeholder="Select payload operators"
            :multiselect="true"
            :value-transformation="item => item.email"
            :display-transformation="
              item => `${item.firstname} ${item.lastname}`
            "
          />

          <validated-dropdown-input
            v-model="selectedDroneRegistrationNumbers"
            @input="onDronesSelected"
            :options="drones"
            label="RPAS"
            name="dronemodel"
            placeholder="Select RPAS"
            validation-rules="required"
            :multiselect="true"
            :value-transformation="item => item.registrationnumber"
            :display-transformation="
              item =>
                item.nickname
                  ? item.nickname
                  : `${item.manufacturer} ${item.model}`
            "
          />
        </md-card-content>
      </md-card>
    </template>

    <template slot="footer">
      <md-button class="md-success md-simple" @click="validate">
        {{ $t("Save") }}
      </md-button>
      <md-button
        v-if="!isNewProject"
        class="md-danger md-simple"
        @click="deleteProject"
      >
        {{ $t("Delete") }}
      </md-button>
    </template>
  </modal>
</template>

<script>
import { Modal } from "@/components";
import ValidatedTextInput from "@/components/Inputs/ValidatedTextInput.vue";
import ValidatedDropdownInput from "@/components/Inputs/ValidatedDropdownInput.vue";
import { mapMultiRowFields } from "vuex-map-fields";

export default {
  $_veeValidate: {
    validator: "new"
  },
  components: {
    ValidatedDropdownInput,
    ValidatedTextInput,
    Modal
  },
  props: {
    project: Object,
    projectPosition: Number
  },
  data() {
    return {
      projectName: "",
      validPilots: [],
      validSuperUsers: [],
      observers: [],
      payloadOperators: [],
      selectedPilotEmails: [],
      selectedSuperuserEmails: [],
      selectedObserverEmails: [],
      selectedPayloadOperatorEmails: [],
      selectedDroneRegistrationNumbers: []
    };
  },
  computed: {
    ...mapMultiRowFields("setting", ["drones", "pilots"]),
    isNewProject() {
      return !this.project;
    }
  },
  beforeMount() {
    this.pilots.forEach(pilot => {
      if (!pilot.email) {
        this.$emit("showNoPilotEmailsWarning");
        return;
      }

      if (pilot.superuser) {
        this.validSuperUsers.push(pilot);
      }

      if (pilot.pilot) {
        this.validPilots.push(pilot);
      }

      if (pilot.observer) {
        this.observers.push(pilot);
      }
      if (pilot.payloadOperator) {
        this.payloadOperators.push(pilot);
      }
    });

    if (this.project) {
      this.projectName = this.project.name;
      this.selectedPilotEmails = this.project.pilots
        ? this.project.pilots.map(pilot => pilot.email)
        : [];
      this.selectedSuperuserEmails = this.project.superusers
        ? this.project.superusers.map(pilot => pilot.email)
        : [];
      this.selectedObserverEmails = this.project.observers
        ? this.project.observers.map(observer => observer.email)
        : [];
      this.selectedPayloadOperatorEmails = this.project.payloadOperators
        ? this.project.payloadOperators.map(
            payloadOperator => payloadOperator.email
          )
        : [];
      this.selectedDroneRegistrationNumbers = this.project.drones
        ? this.project.drones.map(drone => drone.registrationnumber)
        : [];
    }
  },
  methods: {
    closeProjectEditor() {
      this.$emit("closeProjectEditor");
    },
    async validate() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) return;

      if (this.isNewProject) {
        this.addProjectValidated();
      } else {
        this.updateProjectValidated();
      }
      this.$emit("closeProjectEditor");
    },
    addProjectValidated() {
      const {
        selectedPilots,
        selectedSuperusers,
        selectedObservers,
        selectedPayloadOperators,
        selectedDrones
      } = this.prepareProject();

      this.$store.dispatch("setting/addProject", {
        position: this.projectPosition,
        name: this.projectName,
        pilots: selectedPilots,
        superusers: selectedSuperusers,
        observers: selectedObservers,
        payloadOperators: selectedPayloadOperators,
        drones: selectedDrones
      });
    },
    updateProjectValidated() {
      const {
        selectedPilots,
        selectedSuperusers,
        selectedObservers,
        selectedPayloadOperators,
        selectedDrones
      } = this.prepareProject();

      this.$store.dispatch("setting/updateProject", {
        position: this.projectPosition,
        name: this.projectName,
        pilots: selectedPilots,
        superusers: selectedSuperusers,
        observers: selectedObservers,
        payloadOperators: selectedPayloadOperators,
        drones: selectedDrones
      });
    },
    async deleteProject() {
      await this.$store.dispatch("setting/deleteProject", {
        position: this.projectPosition
      });
      this.$emit("closeProjectEditor");
    },
    onPilotsSelected(pilots) {
      this.selectedPilotEmails = pilots;
    },
    onSuperusersSelected(pilots) {
      this.selectedSuperuserEmails = pilots;
    },
    onPayloadOperatorsSelected(payloadOperators) {
      this.selectedPayloadOperatorEmails = payloadOperators;
    },
    onObserversSelected(observers) {
      this.selectedObserverEmails = observers;
    },
    onDronesSelected(drones) {
      this.selectedDroneRegistrationNumbers = drones;
    },
    prepareProject() {
      const selectedPilots = this.mapSelectedUsers(
        this.pilots.filter(pilot =>
          this.selectedPilotEmails.includes(pilot.email)
        )
      );
      const selectedSuperusers = this.mapSelectedUsers(
        this.pilots.filter(pilot =>
          this.selectedSuperuserEmails.includes(pilot.email)
        )
      );
      const selectedObservers = this.mapSelectedUsers(
        this.pilots.filter(pilot =>
          this.selectedObserverEmails.includes(pilot.email)
        )
      );
      const selectedPayloadOperators = this.mapSelectedUsers(
        this.pilots.filter(pilot =>
          this.selectedPayloadOperatorEmails.includes(pilot.email)
        )
      );

      const selectedDrones = this.drones
        .filter(drone =>
          this.selectedDroneRegistrationNumbers.includes(
            drone.registrationnumber
          )
        )
        .map(function(drone) {
          const mappedDrone = {
            manufacturer: drone.manufacturer,
            model: drone.model,
            registrationnumber: drone.registrationnumber,
            color: drone.color,
            certificateFile: drone.certificateFile,
            availability: drone.availability,
            threshold: drone.threshold,
            wilcoid: drone.wilcoid,
            pilots: drone.pilots
          };
          if (drone.nickname && drone.nickname !== "") {
            mappedDrone.nickname = drone.nickname;
          }

          return mappedDrone;
        });

      return {
        selectedPilots,
        selectedSuperusers,
        selectedObservers,
        selectedPayloadOperators,
        selectedDrones
      };
    },
    mapSelectedUsers(users) {
      return users.map(user => ({
        firstname: user.firstname,
        lastname: user.lastname,
        email: user.email,
        license: user.license,
        phone: user.phone,
        pilot: user.pilot,
        observer: user.observer,
        payloadOperator: user.payloadOperator,
        threshold: user.threshold
      }));
    }
  }
};
</script>
